import { useEffect, useRef, type ReactNode } from 'react'

import { Icon } from '../../components/Icon'

/**
 * A right-hand detail panel over a scrim (M23-T03), drawn on `1182:6922`.
 *
 * **Nothing in the admin has used a drawer before**, and it is the one pattern
 * here that is easy to get subtly wrong, so the dialog behaviour is built once
 * in this file rather than five times in the page tickets: focus moves in,
 * focus is trapped, `Escape` closes, focus returns to whatever opened it, the
 * scrim closes on click, and the page behind does not scroll.
 *
 * Returning focus matters more than it sounds. Without it, closing the drawer
 * drops a keyboard user back at the top of the document — after acting on the
 * fourth row of a long queue, that is a lost place and a re-scroll every time.
 */
export function DetailDrawer({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return

    returnTo.current = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusables = (): HTMLElement[] =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      )

    focusables()[0]?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const first = items[0]!
      const last = items[items.length - 1]!
      const active = document.activeElement
      if (e.shiftKey && (active === first || !panelRef.current?.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
      returnTo.current?.focus()
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <div
        role="presentation"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="absolute inset-y-0 right-0 flex w-full max-w-[604px] flex-col overflow-y-auto bg-bg-app shadow-pop"
      >
        <header className="flex items-start justify-between gap-4 px-8 pb-4 pt-8">
          <h2 className="font-display text-xl font-semibold text-ink-1">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-ink-3 hover:bg-bg-soft hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Icon name="x" size={16} />
          </button>
        </header>
        <div className="flex-1 px-8 pb-6">{children}</div>
        {footer !== undefined && <div className="px-8 pb-8">{footer}</div>}
      </div>
    </div>
  )
}

/** A label/value row, as the drawer frame lays them out. */
export function DrawerField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-6 border-b border-line-1 py-3.5 last:border-b-0">
      <dt className="shrink-0 text-sm text-ink-3">{label}</dt>
      <dd className="min-w-0 truncate text-right text-sm font-medium text-ink-1">
        {children}
      </dd>
    </div>
  )
}
